import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '@/services';
import { useIsMobile } from '@/context/IsMobileContext.tsx';
import '@/styles/form-surface.css';
import { PropertyDetailsStep } from './PropertyDetailsStep.tsx';
import { WelcomeStep } from './WelcomeStep.tsx';
import { WizardSuccessStep } from './WizardSuccessStep.tsx';

type TWizardPane = 'welcome' | 'details' | 'done';

/**
 * Blocking first-login onboarding wizard for clients who have not completed
 * their profile. `open` is captured once on mount: submitting the details
 * pane flips profile_completed in the query caches immediately, and a
 * live-derived condition would close the modal before the success pane.
 */
export const WelcomeWizardModal = () => {
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: authApi.getCurrentUser,
  });

  const [open, setOpen] = useState(() => !user?.profile?.profile_completed);
  const [pane, setPane] = useState<TWizardPane>('welcome');

  if (!user || !open) return null;

  const handleFinish = () => {
    setOpen(false);
    navigate('/client/survey');
  };

  return (
    <Modal
      open
      centered
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      width={isMobile ? '100%' : 640}
      styles={{ body: { padding: 0 } }}
      wrapClassName='onboarding-wizard'
      // Same control scale as the account settings forms.
      classNames={{ content: 'whm-form-surface' }}
    >
      {pane === 'welcome' && (
        <WelcomeStep firstName={user.first_name} onNext={() => setPane('details')} />
      )}
      {pane === 'details' && (
        <PropertyDetailsStep
          user={user}
          onBack={() => setPane('welcome')}
          onDone={() => setPane('done')}
        />
      )}
      {pane === 'done' && <WizardSuccessStep firstName={user.first_name} onFinish={handleFinish} />}
    </Modal>
  );
};
